import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Loader2, TrendingUp, Users, GraduationCap, CheckCircle2 } from "lucide-react";

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-white/4 border border-white/10 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-white/50">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-[hsl(var(--accent))]" />}
      </div>
      <div className="font-serif text-3xl font-medium" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/stats").then((r) => setData(r.data));
  }, []);

  if (!data) return <AdminLayout title="Dashboard"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></AdminLayout>;

  const maxChart = Math.max(...data.attempts_last_14_days.map((d) => d.count), 1);

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Total users" value={data.total_users} sub={`${data.active_users_7d} active in 7d`} icon={Users} />
        <Stat label="Exams taken" value={data.exams_taken} sub={`${data.exams_passed} passed`} icon={GraduationCap} />
        <Stat label="Pass rate" value={`${data.pass_rate}%`} sub={data.pass_rate >= 75 ? "Above target" : "Below 75%"} icon={TrendingUp} />
        <Stat label="Questions" value={data.total_questions} sub={`${data.questions_with_audio} audio · ${data.questions_with_image} img`} icon={CheckCircle2} />
      </div>

      {/* Chart */}
      <div className="bg-white/4 border border-white/10 rounded-lg p-6 mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">// exam attempts · last 14 days</div>
        <div className="flex items-end gap-1.5 h-40" data-testid="attempts-chart">
          {data.attempts_last_14_days.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full bg-[hsl(var(--accent))]/80 hover:bg-[hsl(var(--accent))] rounded-t transition-colors relative group"
                style={{ height: `${(d.count / maxChart) * 100}%`, minHeight: d.count > 0 ? 4 : 1 }}>
                {d.count > 0 && (
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono opacity-0 group-hover:opacity-100">{d.count}</span>
                )}
              </div>
              <div className="text-[10px] font-mono text-white/40">{d.date}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Most failed */}
      <div className="bg-white/4 border border-white/10 rounded-lg p-6">
        <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">// top failed questions</div>
        {data.top_failed_questions.length === 0 ? (
          <div className="text-sm text-white/40 italic">No failed answers yet.</div>
        ) : (
          <div className="space-y-2" data-testid="top-failed-list">
            {data.top_failed_questions.map((q) => (
              <div key={q.id} onClick={() => nav(`/admin/questions/${q.id}`)}
                className="flex items-center gap-4 px-4 py-3 rounded-md border border-white/8 hover:border-[hsl(var(--accent))] cursor-pointer">
                <div className="font-mono text-xs text-white/50 shrink-0">{q.category_code}</div>
                <div className="flex-1 text-sm truncate">{q.text_pt}</div>
                <div className="font-mono text-xs text-red-400 shrink-0">{q.wrong_count} wrong</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
